import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { KeychainIcon } from '@/src/renderer/components/icons'
import { InboxIcon } from '@/src/renderer/components/icons/InboxIcon'
import { Button } from '@/src/renderer/components/ui/Button'
import { Input } from '@/src/renderer/components/ui/Input'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useLanguage } from '@/src/renderer/contexts/LanguageProvider'
import type { ForgotPasswordFormProps } from './ForgotPasswordForm.types'
import './ForgotPasswordForm.scss'

export function ForgotPasswordForm({ onBackToLogin }: ForgotPasswordFormProps) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const account = useAccountSource()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(30)
  const emailInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus email input when component mounts
  useEffect(() => {
    emailInputRef.current?.focus()
  }, [])

  // Keyboard shortcuts: ESC to go back, Enter to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onBackToLogin()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onBackToLogin])

  // Countdown timer for resend button
  useEffect(() => {
    if (!success || canResend) return

    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true)
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [success, canResend])

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const translateSupabaseError = (errorMessage: string): string => {
    // Client-side cooldown (exponential backoff between same-user requests)
    if (errorMessage.includes('For security purposes')) {
      return t('auth.forgotPassword.errorRateLimit')
    }
    // Server-side email send rate limit (Supabase over_email_send_rate_limit)
    if (errorMessage.includes('email rate limit exceeded')) {
      return t('auth.forgotPassword.errorEmailRateLimit')
    }
    return errorMessage
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email
    if (!email || !isValidEmail(email)) {
      setEmailError(true)
      emailInputRef.current?.focus()
      return
    }

    setEmailError(false)
    setLoading(true)
    setError('')
    setSuccess(false)

    const result = await account.requestPasswordReset(email, language)

    if (result.success) {
      setSuccess(true)
      setResendCountdown(30)
      setCanResend(false)
    } else {
      const translatedError = result.error ? translateSupabaseError(result.error) : t('auth.forgotPassword.errorGeneric')
      setError(translatedError)
    }

    setLoading(false)
  }

  const handleResend = async () => {
    if (!canResend || !email) return

    setCanResend(false)
    setResendCountdown(30)
    setError('')

    const result = await account.requestPasswordReset(email, language)

    if (!result.success) {
      const translatedError = result.error ? translateSupabaseError(result.error) : t('auth.forgotPassword.errorGeneric')
      setError(translatedError)
      // Reset countdown if failed
      setCanResend(true)
      setResendCountdown(0)
    }
  }

  // Success screen after email sent
  if (success) {
    return (
      <div className="forgot-password-form forgot-password-form--success">
        <div className="forgot-password-form__success-content">
          <InboxIcon size={100} className="forgot-password-form__icon" />

          <h2 className="forgot-password-form__success-title">
            {t('auth.forgotPassword.successTitle')}
          </h2>

          <p className="forgot-password-form__success-subtitle">
            {t('auth.forgotPassword.successSubtitle', { email })}
          </p>

          {error && (
            <div className="forgot-password-form__error">{error}</div>
          )}

          <div className="forgot-password-form__resend-section">
            <span className="forgot-password-form__resend-text">
              {t('auth.forgotPassword.noEmail')}{' '}
            </span>
            {canResend ? (
              <button
                className="forgot-password-form__resend-link"
                onClick={handleResend}
                type="button"
              >
                {t('auth.forgotPassword.resend')}
              </button>
            ) : (
              <span className="forgot-password-form__resend-countdown">
                {t('auth.forgotPassword.resendIn', { seconds: resendCountdown })}
              </span>
            )}
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={onBackToLogin}
            shortcut={{ kind: 'character', text: 'ESC' }}
          >
            {t('auth.forgotPassword.backToLogin')}
          </Button>
        </div>
      </div>
    )
  }

  // Initial form screen
  return (
    <div className="forgot-password-form">
      <div className="forgot-password-form__header">
        <KeychainIcon size={100} className="forgot-password-form__icon" />
        <h2 className="forgot-password-form__title">
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="forgot-password-form__subtitle">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      <form className="forgot-password-form__form" onSubmit={handleSubmit} noValidate>
        <div className="forgot-password-form__input-group">
          <Input
            ref={emailInputRef}
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={t('auth.forgotPassword.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setEmailError(false)
            }}
            disabled={loading}
            error={emailError}
          />
          {emailError && (
            <p className="forgot-password-form__error-message">
              {t('setup.emailInvalid')}
            </p>
          )}
        </div>

        {error && (
          <div className="forgot-password-form__error">{error}</div>
        )}

        <div className="forgot-password-form__actions">
          <Button
            type="submit"
            disabled={loading}
            shortcut={{ kind: 'return' }}
          >
            {loading
              ? t('auth.forgotPassword.submitting')
              : t('auth.forgotPassword.submit')}
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={onBackToLogin}
            shortcut={{ kind: 'character', text: 'ESC' }}
          >
            {t('auth.forgotPassword.backToLogin')}
          </Button>
        </div>
      </form>
    </div>
  )
}
